import TextField from "./WorksheetTextField";
import { Box, Button, MenuItem, Typography } from "@mui/material";
import { CostRow, Worksheet, measurementUnit } from "./calculator";
import { worksheetFieldSx, worksheetNumberProps as numberProps } from "./worksheetStyles";
type Props = {
  row: CostRow;
  item: Worksheet;
  onChange: (patch: Partial<CostRow>) => void;
  readOnly: boolean;
  busy: boolean;
  settings: any;
};
export default function WorksheetMaterialCalculation({
  row,
  item,
  onChange,
  readOnly,
  busy,
  settings,
}: Props) {
  const linkedUnit = measurementUnit(item.unit);
  const sizeUnit =
    row.jobSizeUnit ??
    (linkedUnit === "lin_ft" && row.calculation !== "coverage" ? "sq_ft" : linkedUnit) ??
    item.areaUnit ??
    "sq_ft";
  const legacySeparate =
    !row.jobSizeUnit &&
    (row.calculation === "coverage" ? !linkedUnit : item.areaSource === "manual");
  const size = row.jobSize ?? (legacySeparate ? item.area : item.quantity) ?? "0";
  return (
    <Box sx={{ p: 1, bgcolor: "#F9FAFC", ...worksheetFieldSx }}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          py: 0.5,
          "& .MuiTextField-root": { width: { xs: "100%", sm: 210 }, minWidth: 0 },
        }}
      >
        <TextField
          select
          size="small"
          label="Quantity calculation"
          value={row.calculation}
          disabled={readOnly || busy}
          onChange={(e) => {
            const calculation = e.target.value as CostRow["calculation"];
            const weight = calculation === "density" || calculation === "thickness_factor";
            const unit =
              weight &&
              !["ton", "tons", "us_ton", "lb", "lbs", "pound", "pounds"].includes(
                row.unit.toLowerCase()
              )
                ? "Ton"
                : row.unit;
            const measurement = weight && linkedUnit === "lin_ft" ? "sq_ft" : linkedUnit ?? "sq_ft";
            onChange({
              calculation,
              unit,
              jobSize: null,
              jobSizeUnit: measurement,
              factorAreaUnit: calculation === "coverage" ? measurement : row.factorAreaUnit,
              quantityOverride: null,
            });
          }}
        >
          <MenuItem value="manual">Manual quantity</MenuItem>
          <MenuItem value="coverage">Coverage</MenuItem>
          <MenuItem value="density">Weight — density</MenuItem>
          <MenuItem value="thickness_factor">Weight — thickness-specific factor</MenuItem>
        </TextField>
        {row.calculation !== "manual" && (
          <>
            <TextField
              size="small"
              type="text"
              label="Quantity / Job Size"
              inputProps={{ ...numberProps, inputMode: "decimal" }}
              value={size}
              disabled={readOnly || busy}
              helperText={
                row.jobSize != null || legacySeparate
                  ? "Job-size override"
                  : "Linked to line-item quantity"
              }
              onChange={(e) => onChange({ jobSize: e.target.value, jobSizeUnit: sizeUnit })}
            />
            {(row.jobSize != null || legacySeparate) && !readOnly && (
              <Button
                disabled={busy}
                onClick={() => onChange({ jobSize: null, jobSizeUnit: sizeUnit })}
              >
                Use line-item quantity
              </Button>
            )}
            <TextField
              select
              size="small"
              label="Job size unit"
              value={sizeUnit}
              disabled={readOnly || busy}
              onChange={(e) =>
                onChange({
                  jobSizeUnit: e.target.value,
                  jobSize: legacySeparate ? size : row.jobSize,
                })
              }
            >
              <MenuItem value="sq_ft">Sq Ft</MenuItem>
              <MenuItem value="sq_yd">Sq Yd</MenuItem>
              {row.calculation === "coverage" && <MenuItem value="lin_ft">Lin Ft</MenuItem>}
            </TextField>
            <TextField
              size="small"
              type="number"
              inputProps={numberProps}
              label={
                row.calculation === "coverage"
                  ? "Coverage Area"
                  : row.calculation === "density"
                  ? "Density (lb/cu ft)"
                  : "US tons/sq ft (thickness included)"
              }
              value={row.factor ?? ""}
              disabled={readOnly || busy}
              onChange={(e) =>
                onChange({
                  factor: e.target.value,
                  factorLabel: null,
                })
              }
            />
            {row.calculation === "coverage" && (
              <TextField
                select
                size="small"
                label="Coverage unit"
                value={row.factorAreaUnit ?? "sq_ft"}
                disabled={readOnly || busy}
                onChange={(e) => onChange({ factorAreaUnit: e.target.value })}
              >
                <MenuItem value="sq_ft">Sq Ft</MenuItem>
                <MenuItem value="sq_yd">Sq Yd</MenuItem>
                <MenuItem value="lin_ft">Lin Ft</MenuItem>
              </TextField>
            )}
            {row.calculation === "density" && (
              <TextField
                size="small"
                type="number"
                inputProps={numberProps}
                label="Thickness (inches)"
                value={row.thickness ?? ""}
                disabled={readOnly || busy}
                onChange={(e) => onChange({ thickness: e.target.value })}
              />
            )}
            {row.calculation === "thickness_factor" && (
              <TextField
                select
                size="small"
                label="Use organization factor"
                value=""
                disabled={readOnly || busy}
                sx={{ minWidth: 200 }}
                onChange={(e) => {
                  const factor = settings?.worksheetSettings?.factors?.find(
                    (f: any) => f.label === e.target.value
                  );
                  if (factor)
                    onChange({
                      factor: factor.value,
                      factorLabel: factor.label,
                    });
                }}
              >
                <MenuItem value="">Select a starting value</MenuItem>
                {(settings?.worksheetSettings?.factors ?? []).map((f: any) => (
                  <MenuItem key={f.label} value={f.label}>
                    {f.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </>
        )}
      </Box>
      {row.factorLabel && (
        <Typography variant="caption">
          Starting factor: {row.factorLabel} (saved with this item)
        </Typography>
      )}
    </Box>
  );
}
